import { initialServices } from "./initialServices";

const DOC_TYPE_REVERSE_MAP = {
  "1": "Kazakhstan ID",
  "33": "Kazakhstan residence permit",
  "28": "MRZ passport",
  "42": "Other passports",
};

const mapNestedParam = (nestedParam, pipelineItem) => {
  if (nestedParam.paramName === "country_code") {
    const val = pipelineItem.country_selector_value;
    if (val) return { ...nestedParam, enabled: true, value: val, selectValue: val };
    return nestedParam;
  }

  if (nestedParam.paramName === "mirror_preview") {
    const val = pipelineItem.config?.render?.mirror_preview;
    if (val !== undefined) return { ...nestedParam, enabled: !!val, value: val };
    return nestedParam;
  }

  if (pipelineItem.config?.[nestedParam.paramName] !== undefined) {
    const configValue = pipelineItem.config[nestedParam.paramName];
    if (configValue === null) return nestedParam;

    if (nestedParam.paramName === "doc_type") {
      const label = DOC_TYPE_REVERSE_MAP[String(configValue)] || configValue;
      return { ...nestedParam, enabled: true, value: label, selectValue: label };
    }

    if (nestedParam.paramName === "recognition_mode") {
      const label = configValue === 0 ? "One side" : "Two sides";
      return { ...nestedParam, enabled: true, value: label, selectValue: label };
    }

    if (nestedParam.type === "checkbox") {
      return { ...nestedParam, enabled: !!configValue, value: configValue };
    }

    return { ...nestedParam, enabled: true, value: configValue, selectValue: configValue };
  }

  if (pipelineItem[nestedParam.paramName] !== undefined) {
    return { ...nestedParam, enabled: true, value: pipelineItem[nestedParam.paramName] };
  }

  return nestedParam;
};

const mapParam = (param, pipelineItem) => {
  if (param.type === "checkbox-select" && Object.prototype.hasOwnProperty.call(pipelineItem, param.paramName)) {
    const val = pipelineItem[param.paramName];
    const coerced = typeof val === "boolean" ? String(val) : val;
    return { ...param, enabled: true, value: coerced, selectValue: coerced };
  }

  if (param.type === "checkbox" && Object.prototype.hasOwnProperty.call(pipelineItem, param.paramName)) {
    return { ...param, enabled: pipelineItem[param.paramName] };
  }

  if (Object.prototype.hasOwnProperty.call(pipelineItem, param.paramName)) {
    return { ...param, enabled: true, value: pipelineItem[param.paramName] };
  }

  if (pipelineItem.options?.includes(param.paramName) && !(param.params?.length > 0)) {
    return { ...param, enabled: true, value: true };
  }

  if (param.params?.length > 0) {
    const isOptionEnabled = pipelineItem.options?.includes(param.paramName) ?? false;
    const updatedNestedParams = param.params.map((np) => mapNestedParam(np, pipelineItem));
    return { ...param, enabled: isOptionEnabled, value: isOptionEnabled, params: updatedNestedParams };
  }

  return param;
};

const mapConsentService = (service, template, serviceIdsToActivate) => {
  const firstLang = Object.keys(template.ui.translation)[0];
  if (!firstLang || !template.ui.translation[firstLang]) return null;

  const consentText = template.ui.translation[firstLang].ConsentAgreement1 || "";
  let title = "", url = "", linkMask = "";
  const match = consentText.match(/(.*)\[(.*)\]\((.*)\)/);
  if (match?.length >= 4) {
    title = match[1]?.trim() ?? "";
    linkMask = match[2]?.trim() ?? "";
    url = match[3]?.trim() ?? "";
  } else {
    title = consentText;
  }

  return {
    ...service,
    isActive: serviceIdsToActivate.includes(service.id),
    params: service.params.map((param) => {
      if (param.id === "userTitle") return { ...param, value: title };
      if (param.id === "url") return { ...param, value: url };
      if (param.id === "linkMask") return { ...param, value: linkMask };
      return param;
    }),
  };
};

export const activateTemplateServices = (template) => {
  const pipeline = template.pipeline;

  const serviceIdsToActivate = initialServices
    .filter((service) => {
      if (service.paramName === "esign-issue" || service.paramName === "esign-sign") {
        const action = service.paramName.split("-")[1];
        return pipeline.some((item) => item.service_name === "esign" && item.action === action);
      }
      return pipeline.some((item) => item.service_name === service.paramName);
    })
    .map((s) => s.id);

  const updatedServices = initialServices.map((service) => {
    const pipelineItem = pipeline.find((item) => {
      if (service.paramName === "esign-issue" || service.paramName === "esign-sign") {
        const action = service.paramName.split("-")[1];
        return item.service_name === "esign" && item.action === action;
      }
      return item.service_name === service.paramName;
    });

    if (!pipelineItem) return { ...service, isActive: false };

    if (service.paramName === "consent" && template.ui?.translation) {
      const mapped = mapConsentService(service, template, serviceIdsToActivate);
      if (mapped) return mapped;
    }

    return {
      ...service,
      isActive: serviceIdsToActivate.includes(service.id),
      params: (service.params ?? []).map((param) => mapParam(param, pipelineItem)),
    };
  });

  const servicesToEnable = initialServices.filter((s) => serviceIdsToActivate.includes(s.id));

  return { updatedServices, servicesToEnable };
};
